/**
 * Client-safe helpers for matching and merging live Strapi Iterable dynamic-zone rows
 * with cached SSR page sections. Strict matching only — no guessing.
 */

/**
 * Strapi / JSON may put components or nested objects in fields the CMS treats as "text".
 * Blind `String(value)` / template `+` can trigger `ToPrimitive` and throw
 * `TypeError: Cannot convert object to primitive value` (Vue class normalization uses `join`).
 */
export function safeStrapiTextField (value: unknown, fallback = ''): string {
  if (value == null || value === '') return fallback
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed !== '' ? trimmed : fallback
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'true' : fallback
  if (typeof value === 'bigint') return String(value)
  return fallback
}

/**
 * Strapi enumeration / custom fields often serialize as `{ value: ... }`.
 * Unwrap once so placement ids and class strings resolve consistently (live-meta + merge).
 */
export function unwrapStrapiScalar (raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return raw
  if ('value' in (raw as Record<string, unknown>)) {
    return (raw as { value: unknown }).value
  }
  return raw
}

/** Iterable block `messageType` for layout (Strapi enum object or string). */
export function normalizeIterableCampaignMessageType (raw: unknown): 'banner' | 'card' | 'notification' {
  const messageTypeToken = safeStrapiTextField(unwrapStrapiScalar(raw), '').toLowerCase()
  if (messageTypeToken === 'card' || messageTypeToken === 'notification') return messageTypeToken
  return 'banner'
}

/** Strapi `imageDesktopPlacement` for banner layout. */
export function normalizeIterableBannerImageSide (raw: unknown): 'left' | 'right' {
  const imageSideToken = safeStrapiTextField(unwrapStrapiScalar(raw), '').toLowerCase()
  return imageSideToken === 'left' ? 'left' : 'right'
}

/**
 * Iterable Web SDK `metadata.placementId` (string / number / enum-shaped).
 * Must match **`EmbeddedPlacement`** `normalizedPlacementId` / Strapi placement id (**finite int > 0**)
 * so `embeddedMessagesByPlacement[n]` lookups succeed after `onMessagesUpdated`.
 */
export function sdkMetadataPlacementIdToMapKey (raw: unknown): number | null {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw <= 0) return null
    return Math.trunc(raw)
  }
  const asText = safeStrapiTextField(unwrapStrapiScalar(raw), '')
  if (asText === '') return null
  const parsed = parseInt(asText, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return null
  return Math.trunc(parsed)
}

export type IterableLiveStrapiPatch = {
  /** If omitted, merge keeps the block’s existing `showComponent` (cached/SSR). */
  showComponent?: boolean
  options?: Record<string, unknown>
  defaults?: Record<string, unknown>
  messageType?: unknown
  imageDesktopPlacement?: 'left' | 'right'
  iterablePlacement?: unknown
  setPlacementId?: unknown
}

/** One row from `/api/iterable-live-blocks-meta` `blocks` array. */
export type IterableLiveListItem = IterableLiveStrapiPatch & {
  strapiComponentId: string
  placementIdNorm: string | null
}

/** Raw `placementId` from Strapi `iterable-placement` relation (flat or `data.attributes`). */
function rawIterablePlacementIdFromRelation (rel: unknown): unknown {
  if (rel == null || typeof rel !== 'object') return null
  const placementRelation = rel as {
    placementId?: unknown
    data?: { attributes?: { placementId?: unknown } } | null
    attributes?: { placementId?: unknown }
  }
  const raw =
    placementRelation.placementId ??
    placementRelation.data?.attributes?.placementId ??
    placementRelation.attributes?.placementId ??
    null
  return unwrapStrapiScalar(raw)
}

function placementIdFromRelationShape (rel: unknown): string | null {
  return normalizePlacementIdRaw(rawIterablePlacementIdFromRelation(rel))
}

/**
 * Parsed numeric `placementId` for SDK / `EmbeddedPlacement` (finite integer **> 0** only).
 * Keeps live-meta (`iterableLiveStrapi` nested cards) and `CardsProgram` aligned.
 */
export function numericPlacementIdFromIterableRelation (rel: unknown): number | null {
  const raw = rawIterablePlacementIdFromRelation(rel)
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const truncated = Math.trunc(raw)
    return truncated > 0 ? truncated : null
  }
  const asText = safeStrapiTextField(raw, '')
  if (asText === '') return null
  const parsed = parseInt(asText, 10)
  if (Number.isFinite(parsed) && parsed > 0) return parsed
  return null
}

export function normalizePlacementIdRaw (raw: unknown): string | null {
  if (raw == null || raw === '') return null
  const trimmedId = safeStrapiTextField(unwrapStrapiScalar(raw), '')
  return trimmedId !== '' ? trimmedId : null
}

/** Dynamic-zone block from page `sections` (cached SSR). */
export function normalizeIterableBlockId (block: unknown): string | null {
  if (block == null || typeof block !== 'object') return null
  const id = (block as { id?: unknown }).id
  if (id == null || id === '') return null
  if (typeof id === 'object' && id !== null && 'documentId' in id) {
    const documentId = (id as { documentId?: unknown }).documentId
    return safeStrapiTextField(documentId, '') || null
  }
  return safeStrapiTextField(id, '') || null
}

export function normalizePlacementIdFromPageBlock (block: unknown): string | null {
  if (block == null || typeof block !== 'object') return null
  const pageBlockRecord = block as { iterablePlacement?: unknown }
  return placementIdFromRelationShape(pageBlockRecord.iterablePlacement)
}

/**
 * Strict match: prefer unique normalized placementId, then Strapi component id.
 * When several live rows share a placementId (banner + card), fall through to id —
 * do not guess by index.
 */
export function findMatchingLiveIterableBlock (
  pageBlock: unknown,
  liveBlocks: IterableLiveListItem[]
): IterableLiveListItem | null {
  const list = Array.isArray(liveBlocks) ? liveBlocks : []
  if (list.length === 0) return null

  const placementNorm = normalizePlacementIdFromPageBlock(pageBlock)
  if (placementNorm != null) {
    const matches = list.filter(
      (liveBlock) =>
        liveBlock.placementIdNorm != null && liveBlock.placementIdNorm === placementNorm
    )
    if (matches.length === 1) return matches[0]
    // Ambiguous placement (e.g. banner + card both use Home Sandbox) → id match below.
  }

  const blockId = normalizeIterableBlockId(pageBlock)
  if (blockId != null) {
    const matches = list.filter((liveBlock) => liveBlock.strapiComponentId === blockId)
    if (matches.length === 1) return matches[0]
    if (matches.length > 1) return null
  }

  return null
}

/**
 * Iterable-only Card–Programs rows present in live-meta but missing from cached SSR
 * `cards` (CDN / findOneBySlug lag). Append so client hydration can still mount **I** rows.
 */
export function appendMissingLiveCardProgramRows<T extends Record<string, unknown>> (
  cards: T[],
  liveBlocks: IterableLiveListItem[]
): T[] {
  const list = Array.isArray(cards) ? cards : []
  const liveList = Array.isArray(liveBlocks) ? liveBlocks : []
  if (liveList.length === 0) return list

  const presentIds = new Set(
    list
      .map((card) => normalizeIterableBlockId(card))
      .filter((id): id is string => id != null && id !== ''),
  )
  const out = [...list]

  for (const live of liveList) {
    if (normalizeIterableCampaignMessageType(live.messageType) !== 'card') continue
    const liveId = live.strapiComponentId
    if (liveId == null || liveId === '' || presentIds.has(liveId)) continue
    if (live.placementIdNorm == null || live.iterablePlacement == null) continue
    if (numericPlacementIdFromIterableRelation(live.iterablePlacement) == null) continue

    out.push({
      id: liveId,
      messageType: 'card',
      iterablePlacement: live.iterablePlacement,
    } as T)
    presentIds.add(liveId)
  }

  return out
}

function shallowMergeRecord (
  prev: unknown,
  incoming: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (incoming === undefined) return undefined
  const base =
    prev != null && typeof prev === 'object' && !Array.isArray(prev)
      ? { ...(prev as Record<string, unknown>) }
      : {}
  return { ...base, ...incoming }
}

/**
 * Non-destructive patch: shallow-clone block, then patch only known Iterable fields.
 * Options/defaults are shallow-merged to preserve keys not present in live payload.
 */
export function mergeIterableDynamicZoneBlock<T extends Record<string, unknown>> (
  block: T,
  live: IterableLiveListItem
): T {
  if (block == null || typeof block !== 'object') {
    return block as T
  }
  const {
    strapiComponentId: _unusedStrapiComponentId,
    placementIdNorm: _unusedPlacementIdNorm,
    ...patch
  } = live
  void _unusedStrapiComponentId
  void _unusedPlacementIdNorm

  const out: Record<string, unknown> = { ...block }

  if (typeof patch.showComponent === 'boolean') {
    out.showComponent = patch.showComponent
  }

  const mergedOptions = shallowMergeRecord(block.options, patch.options)
  if (mergedOptions !== undefined) out.options = mergedOptions

  const mergedDefaults = shallowMergeRecord(block.defaults, patch.defaults)
  if (mergedDefaults !== undefined) out.defaults = mergedDefaults

  if (patch.messageType !== undefined) {
    out.messageType = normalizeIterableCampaignMessageType(patch.messageType)
  }
  if (patch.imageDesktopPlacement !== undefined) {
    out.imageDesktopPlacement = normalizeIterableBannerImageSide(patch.imageDesktopPlacement)
  }
  if (patch.iterablePlacement !== undefined) {
    out.iterablePlacement = patch.iterablePlacement
  }
  if (patch.setPlacementId !== undefined) {
    out.setPlacementId = patch.setPlacementId
  }

  return out as T
}
